import {
  Grid,
  Stack,
  Box,
  alpha,
  Typography,
  Button,
  useTheme,
  Skeleton,
} from '@mui/material';
import VolSkewChart from 'components/implied_vol/VolSkew/VolSkewChart';
import { VolSkewProvider } from 'components/implied_vol/VolSkew';
import OptionsDropdownSelector from 'components/shared/OptionsDropdownSelector';
import SliderFilter from 'components/shared/SliderFilter';
import { IVOL_SKEW_BASE_LINE_LABELS_MAP } from 'config/iVol';
import { useCallback } from 'react';
import { DEFAULT_VOL_SKEW_CONFIG, VolSkewReferenceLine } from 'types';
import {
  IVAdjustmentSettings,
  OC_DEFAULT_CALL_SLOPE_MULT,
  OC_DEFAULT_LEVEL_SHIFT,
  OC_DEFAULT_PUT_SLOPE_MULT,
} from 'types/optionsCalculator';
import { addLeadingSign, formatAsCompactNumber } from 'util/shared';

interface IVSettingsProps {
  sym: string;
  ivSettings: IVAdjustmentSettings;
  currentReferenceLine: VolSkewReferenceLine;
  onUpdateIvSettings: (settings: IVAdjustmentSettings) => void;
  onUpdateCurrentReferenceLine: (line: VolSkewReferenceLine) => void;
  defaultExpiration: number;
  currentAtmIV?: number;
}

const IVSettings = ({
  sym,
  ivSettings,
  onUpdateIvSettings,
  currentReferenceLine,
  onUpdateCurrentReferenceLine,
  defaultExpiration,
  currentAtmIV,
}: IVSettingsProps) => {
  const theme = useTheme();

  const displayValueFormatter = useCallback((value: number | number[]) => {
    if (value == null || Array.isArray(value)) {
      return '';
    }
    return `${formatAsCompactNumber(value, { maximumFractionDigits: 2 })}x`;
  }, []);

  const displayLevelShiftValueFormatter = useCallback(
    (value: number | number[]) => {
      if (value == null || Array.isArray(value)) {
        return '';
      }
      return `${addLeadingSign(value * 100)} vol points`;
    },
    [],
  );

  const handleResetToDefaults = useCallback(() => {
    onUpdateIvSettings({
      ...ivSettings,
      level_shift: OC_DEFAULT_LEVEL_SHIFT,
      call_slope_mult: OC_DEFAULT_CALL_SLOPE_MULT,
      put_slope_mult: OC_DEFAULT_PUT_SLOPE_MULT,
    });
  }, [onUpdateIvSettings, ivSettings]);

  if (currentAtmIV == null) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          p: 4,
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
          <Skeleton variant="rectangular" width="100%" height={60} />
          <Skeleton variant="rectangular" width="80%" height={40} />
          <Skeleton variant="rectangular" width="60%" height={40} />
        </Stack>
      </Box>
    );
  }

  // Calculate the maximum IV range for the slider based on current ATM IV
  // We use 3x the current ATM IV as a reasonable upper bound for adjustments
  const rawMaxIV = currentAtmIV * 3;

  // Dynamic step calculation for IV Shift slider with proper divisibility
  // This ensures smooth slider operation and appropriate granularity based on the IV range
  const getIVShiftStepAndMax = (rawMax: number) => {
    // Define step sizes based on IV magnitude to provide appropriate granularity
    // Smaller IVs get finer steps, larger IVs get coarser steps for usability
    const stepRanges = [
      { threshold: 1, step: 0.005 }, // Very fine steps for low IV (0.5 vol points)
      { threshold: 2, step: 0.01 }, // Fine steps for moderate IV (1 vol point)
      { threshold: 5, step: 0.025 }, // Medium steps for higher IV (2.5 vol points)
      { threshold: 10, step: 0.05 }, // Coarse steps for high IV (5 vol points)
      { threshold: Infinity, step: 0.1 }, // Very coarse steps for extremely high IV (10 vol points)
    ];

    // Find the appropriate step size based on the raw maximum IV value
    const { step } = stepRanges.find((range) => rawMax <= range.threshold)!;

    // Round the maximum IV up to the nearest step increment to ensure clean slider bounds
    // This prevents floating point precision issues and ensures the max value is reachable
    const maxIV = Math.ceil(rawMax / step) * step;

    return { step, maxIV };
  };

  // Calculate the final step size and maximum IV value for the slider
  const { step: ivShiftStep, maxIV } = getIVShiftStepAndMax(rawMaxIV);

  return (
    <Grid
      container
      sx={{
        height: '100%',
        p: 2,
        minHeight: {
          xs: '900px',
          md: '450px',
        },
      }}
      spacing={6}
    >
      <Grid item xs={12} md={3}>
        <Stack sx={{ gap: 4 }}>
          {/* Reference Line Selection Section */}
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              p: 4,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 4,
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              Select a Reference Line
            </Typography>
            <OptionsDropdownSelector
              label="Reference Line"
              value={currentReferenceLine}
              options={Object.values(VolSkewReferenceLine).map((line) => ({
                label: IVOL_SKEW_BASE_LINE_LABELS_MAP[line],
                value: line,
              }))}
              isMultiple={false}
              onChange={(event) =>
                onUpdateCurrentReferenceLine(
                  event.target.value as VolSkewReferenceLine,
                )
              }
              sx={{ maxWidth: '100%' }}
            />
          </Box>

          {/* IV Adjustments Section */}
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              p: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                Adjust the IV Curve
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetToDefaults}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  px: 2,
                }}
              >
                Reset
              </Button>
            </Box>
            <Stack sx={{ gap: 2 }}>
              <SliderFilter
                header="IV Shift"
                name="levelShift"
                min={-maxIV}
                max={maxIV}
                step={ivShiftStep}
                value={ivSettings.level_shift}
                onChange={(newValue) =>
                  onUpdateIvSettings({
                    ...ivSettings,
                    level_shift: newValue as number,
                  })
                }
                valueFormat={'number'}
                displayValueFormatter={displayLevelShiftValueFormatter}
                showStepButtons
              />
              <SliderFilter
                header="Call Skew (Flatten/Steepen)"
                name="callSkew"
                min={0.1}
                max={5.0}
                step={0.05}
                value={ivSettings.call_slope_mult}
                onChange={(newValue) =>
                  onUpdateIvSettings({
                    ...ivSettings,
                    call_slope_mult: newValue as number,
                  })
                }
                valueFormat={'number'}
                displayValueFormatter={displayValueFormatter}
                showStepButtons
              />
              <SliderFilter
                header="Put Skew (Flatten/Steepen)"
                name="putSkew"
                min={0.1}
                max={5.0}
                step={0.05}
                value={ivSettings.put_slope_mult}
                onChange={(newValue) =>
                  onUpdateIvSettings({
                    ...ivSettings,
                    put_slope_mult: newValue as number,
                  })
                }
                valueFormat={'number'}
                displayValueFormatter={displayValueFormatter}
                showStepButtons
              />
            </Stack>
          </Box>
        </Stack>
      </Grid>

      <Grid item xs={12} md={9}>
        <VolSkewProvider
          selectedSym={sym}
          config={{ ...DEFAULT_VOL_SKEW_CONFIG, useSpotGammaIV: true }}
          onConfigChange={() => {}}
        >
          <VolSkewChart
            defaultExpiration={defaultExpiration}
            ivAdjustmentParams={ivSettings}
            referenceLine={currentReferenceLine}
            forceReset
            bypassAccessCheck
          />
        </VolSkewProvider>
      </Grid>
    </Grid>
  );
};

export default IVSettings;
